/**
 * Iter XX.266 — More sheet.
 *
 * Bottom-anchored overlay listing the secondary destinations
 * previously crammed into the crowded header pill row.
 *
 *   · Season (opens the SeasonCard modal — passed in as `onOpenSeason`)
 *   · Earn   (link → /earn)
 *   · Journey (link → /journey — the founder-labelled "Your Cricket")
 *   · Timeline (link → /timeline)
 *   · Settings/Support/Account destinations are rendered as-is when
 *     they exist in the route table; missing routes are silently
 *     omitted so we never render dead links.
 *
 * Design invariants:
 *   · Dark neutral base, single emerald hairline separators only on
 *     Plus row so "Your subscription" reads as first-class.
 *   · Every destination is an existing route/handler — no duplicated
 *     product surfaces. Deferred XX.266 nav destinations that don't
 *     yet exist are omitted rather than mocked.
 *   · Backdrop + swipe-to-close style behaviour preserved by shadcn
 *     Sheet primitives already in `/components/ui/sheet.tsx`.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Gift, CalendarClock, ClipboardList,
  UserCircle, HelpCircle, X, Sparkles,
} from "lucide-react";

const closeAll = (onClose) => { if (typeof onClose === "function") onClose(); };

function Row({ icon: Icon, label, subtitle, to, onClick, testid, accent = false }) {
  const inner = (
    <>
      <div className={
        "w-9 h-9 rounded-xl flex items-center justify-center shrink-0 " +
        (accent
          ? "bg-emerald-600/15 border border-emerald-700/40 text-emerald-300"
          : "bg-neutral-800 text-neutral-300")
      }>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={"text-sm font-medium " + (accent ? "text-emerald-100" : "text-neutral-100")}>
          {label}
        </div>
        {subtitle && <div className="text-xs text-neutral-500 mt-0.5">{subtitle}</div>}
      </div>
    </>
  );
  const cls =
    "w-full flex items-center gap-3 py-3 px-3 rounded-xl " +
    "hover:bg-neutral-800/60 transition-colors text-left";
  if (to) {
    return (
      <Link to={to} onClick={onClick} data-testid={testid} className={cls}>{inner}</Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>
  );
}

export default function MoreSheet({
  open,
  onClose,
  onOpenSeason,
  isPlus = false,
  showEarn = true,
  showJourney = true,
  showSeason = true,
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      data-testid="mobile-more-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="More options"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => closeAll(onClose)}
        aria-hidden="true"
      />
      <div
        className="relative bg-neutral-950 border-t border-neutral-800 rounded-t-3xl px-3 pt-3 pb-3 max-w-2xl mx-auto w-full"
        style={{ paddingBottom: "calc(var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)) + 12px)" }}
      >
        {/* Grabber */}
        <div className="flex justify-center mb-1">
          <span className="w-10 h-1 rounded-full bg-neutral-700" aria-hidden="true" />
        </div>
        <div className="flex items-center justify-between px-2 pt-1 pb-2">
          <div className="text-base font-semibold text-neutral-50">More</div>
          <button
            type="button"
            onClick={() => closeAll(onClose)}
            aria-label="Close more menu"
            className="p-1 -mr-1 text-neutral-400 hover:text-white"
            data-testid="mobile-more-sheet-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-0.5">
          {/* Plus status row — first-class placement. */}
          <Row
            icon={Sparkles}
            label={isPlus ? "Gully Plus" : "Get Gully Plus"}
            subtitle={isPlus ? "Your subscription is active" : "Unlimited reviews, patterns, reflections"}
            to="/plus"
            onClick={() => closeAll(onClose)}
            testid="mobile-more-plus"
            accent
          />

          <div className="h-px bg-neutral-800/70 my-2 mx-2" />

          {showJourney && (
            <Row
              icon={ClipboardList}
              label="Your Cricket"
              subtitle="Journey, focus, missions"
              to="/journey"
              onClick={() => closeAll(onClose)}
              testid="mobile-more-journey"
            />
          )}
          {showSeason && (
            <Row
              icon={Trophy}
              label="Season"
              subtitle="Matches on record this season"
              onClick={() => {
                closeAll(onClose);
                if (typeof onOpenSeason === "function") onOpenSeason();
              }}
              testid="mobile-more-season"
            />
          )}
          {showEarn && (
            <Row
              icon={Gift}
              label="Earn"
              subtitle="Invite cricketers, earn rewards"
              to="/earn"
              onClick={() => closeAll(onClose)}
              testid="mobile-more-earn"
            />
          )}
          <Row
            icon={CalendarClock}
            label="Timeline"
            subtitle="Your Gully activity"
            to="/timeline"
            onClick={() => closeAll(onClose)}
            testid="mobile-more-timeline"
          />

          <div className="h-px bg-neutral-800/70 my-2 mx-2" />

          <Row
            icon={UserCircle}
            label="Account"
            to="/me"
            onClick={() => closeAll(onClose)}
            testid="mobile-more-account"
          />
          <Row
            icon={HelpCircle}
            label="Trust & privacy"
            to="/trust"
            onClick={() => closeAll(onClose)}
            testid="mobile-more-trust"
          />
        </div>
      </div>
    </div>
  );
}
